import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { useToast } from '@/components/toast';
import { useRequireAuth } from '@/hooks/use-require-auth';
import type { WishlistActionResponse } from '@/lib/api/wishlist.server';
import { buildAddToWishlistActionParams } from '@/lib/pending-wishlist-product';
import { useWishlistContext } from '@/providers/wishlist';

export const useWishlist = () => {
    const { t } = useTranslation('product');
    const addFetcher = useFetcher<WishlistActionResponse>();
    const removeFetcher = useFetcher<WishlistActionResponse>();
    const { addToast } = useToast();
    const { wishlists, setWishlists, isProductInAnyWishlist, getProductMembership } = useWishlistContext();

    const [pendingOperation, setPendingOperation] = useState<'add' | 'remove' | null>(null);
    const [optimisticAddedProductIds, setOptimisticAddedProductIds] = useState<Set<string>>(() => new Set());
    const [optimisticRemovedProductIds, setOptimisticRemovedProductIds] = useState<Set<string>>(() => new Set());
    const pendingAddRef = useRef<{ productId: string; productName: string } | null>(null);
    const pendingRemoveRef = useRef<{ productId: string } | null>(null);
    const hasHandledAddRef = useRef(false);
    const hasHandledRemoveRef = useRef(false);

    const isLoading = addFetcher.state !== 'idle' || removeFetcher.state !== 'idle';

    const providerProductIds = useMemo(
        () => new Set(wishlists.flatMap((wishlist) => wishlist.items.map((item) => item.productId))),
        [wishlists]
    );
    const wishlistProductIdSet = useMemo(() => {
        const productIds = new Set([...providerProductIds, ...optimisticAddedProductIds]);
        optimisticRemovedProductIds.forEach((productId) => productIds.delete(productId));
        return productIds;
    }, [optimisticAddedProductIds, optimisticRemovedProductIds, providerProductIds]);
    const wishlistProductIds = useMemo(() => Array.from(wishlistProductIdSet), [wishlistProductIdSet]);

    useEffect(() => {
        if (addFetcher.state === 'submitting') {
            hasHandledAddRef.current = false;
            return;
        }

        if (addFetcher.data && pendingAddRef.current && !hasHandledAddRef.current) {
            hasHandledAddRef.current = true;
            const result = addFetcher.data;
            const { productId, productName } = pendingAddRef.current;
            pendingAddRef.current = null;

            if (result.success) {
                if (result.wishlists) {
                    setWishlists(result.wishlists);
                }
                if (result.alreadyInWishlist) {
                    addToast(t('alreadyInWishlist', { productName }), 'info');
                } else {
                    addToast(t('addedToWishlist', { productName }), 'success');
                }
                setOptimisticRemovedProductIds((current) => {
                    const next = new Set(current);
                    next.delete(productId);
                    return next;
                });
            } else {
                setOptimisticAddedProductIds((current) => {
                    const next = new Set(current);
                    next.delete(productId);
                    return next;
                });
                addToast(t('failedToAddToWishlist'), 'error');
            }
            setPendingOperation(null);
        }
    }, [addFetcher.state, addFetcher.data, addToast, setWishlists, t]);

    useEffect(() => {
        if (removeFetcher.state === 'submitting') {
            hasHandledRemoveRef.current = false;
            return;
        }

        if (removeFetcher.data && pendingRemoveRef.current && !hasHandledRemoveRef.current) {
            hasHandledRemoveRef.current = true;
            const result = removeFetcher.data;
            const { productId } = pendingRemoveRef.current;
            pendingRemoveRef.current = null;

            if (result.success) {
                if (result.wishlists) {
                    setWishlists(result.wishlists);
                }
                setOptimisticAddedProductIds((current) => {
                    const next = new Set(current);
                    next.delete(productId);
                    return next;
                });
                addToast(t('removedFromWishlist'), 'success');
            } else {
                setOptimisticRemovedProductIds((current) => {
                    const next = new Set(current);
                    next.delete(productId);
                    return next;
                });
                setOptimisticAddedProductIds((current) => new Set(current).add(productId));
                addToast(t('failedToRemoveFromWishlist'), 'error');
            }
            setPendingOperation(null);
        }
    }, [removeFetcher.state, removeFetcher.data, addToast, setWishlists, t]);

    const isItemInWishlist = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit'], variant?: ShopperSearch.schemas['ProductSearchHit']) => {
            const productId = variant?.productId || product.productId;
            return Boolean(
                productId &&
                    (wishlistProductIdSet.has(productId) ||
                        (isProductInAnyWishlist(productId) && !optimisticRemovedProductIds.has(productId)))
            );
        },
        [isProductInAnyWishlist, optimisticRemovedProductIds, wishlistProductIdSet]
    );

    const toggleWishlistBase = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit'], variant?: ShopperSearch.schemas['ProductSearchHit']) => {
            const productId = variant?.productId || product.productId;
            if (!productId) {
                addToast(t('failedToAddToWishlist'), 'error');
                return;
            }

            const memberships = getProductMembership(productId);
            const isInWishlist = wishlistProductIdSet.has(productId);

            if (isInWishlist) {
                pendingRemoveRef.current = { productId };
                const membership = memberships[0];
                setOptimisticRemovedProductIds((current) => new Set(current).add(productId));
                setOptimisticAddedProductIds((current) => {
                    const next = new Set(current);
                    next.delete(productId);
                    return next;
                });
                const payload: Record<string, string> = { productId };
                if (membership?.listId) payload.listId = membership.listId;
                if (membership?.itemId) payload.itemId = membership.itemId;
                void removeFetcher.submit(payload, { method: 'POST', action: '/action/wishlist-remove' });
            } else {
                pendingAddRef.current = { productId, productName: product.productName || 'product' };
                setOptimisticAddedProductIds((current) => new Set(current).add(productId));
                setOptimisticRemovedProductIds((current) => {
                    const next = new Set(current);
                    next.delete(productId);
                    return next;
                });
                void addFetcher.submit({ productId }, { method: 'POST', action: '/action/wishlist-add' });
            }
            setPendingOperation(isInWishlist ? 'remove' : 'add');
        },
        [addFetcher, removeFetcher, addToast, getProductMembership, t, wishlistProductIdSet]
    );

    const toggleWishlist = useRequireAuth(toggleWishlistBase as (...args: unknown[]) => Promise<unknown>, {
        actionName: 'addToWishlist',
        getActionParams: (...args: unknown[]) => {
            const product = args[0] as ShopperSearch.schemas['ProductSearchHit'];
            const variant = args[1] as ShopperSearch.schemas['ProductSearchHit'] | undefined;
            return buildAddToWishlistActionParams(product, variant);
        },
        getReturnUrl: () => window.location.pathname + window.location.search,
        toastMessage: t('signInToAddToWishlist'),
    }) as (
        product: ShopperSearch.schemas['ProductSearchHit'],
        variant?: ShopperSearch.schemas['ProductSearchHit']
    ) => Promise<void>;

    return {
        wishlist: wishlistProductIds,
        isLoading,
        pendingOperation,
        isItemInWishlist,
        toggleWishlist,
    };
};
